/**
 * PANNEAU « CLÉ D'ACCÈS » — la logique, sans React.
 *
 * Tout ce qui décide d'un TEXTE ou d'une ÉTAPE vit ici, en fonctions pures,
 * pour être éprouvé par les tests (revue du 15/10/2026) : le mode d'emploi
 * qui faisait nommer la nouvelle clé comme l'ancienne, l'invitation à
 * supprimer l'ancienne clé avant toute lecture réussie avec la nouvelle,
 * « changée aujourd'hui » pour une clé de la veille, l'adresse du port API
 * d'Access présentée comme l'application.
 */

export type IntegrationACle = 'unifi_network' | 'unifi_protect' | 'home_assistant' | 'unifi_access' | 'skello' | 'shotgun' | 'pretix'

export interface EtatCle {
  integration: IntegrationACle
  nom: string
  configuree: boolean
  enregistree_le: string | null
  enregistree_par: { id: string; nom: string } | null
  derniere_reussite: string | null
  reussite_depuis_enregistrement: boolean
  echec_depuis_enregistrement: boolean
  echec_classe: string | null
  /** `consigne` : le conseil sans l'âge (serveur du 16/10/2026) ; absente d'un serveur plus ancien. */
  rotation: { etat: 'INCONNU' | 'OK' | 'A_PREVOIR' | 'EN_RETARD'; age_jours: number | null; conseil: string; consigne?: string }
  adresse: string | null
  organisateur: string | null
}

/* ─────────────────────────────────────────── dates, au calendrier de Paris ─── */

const jourParis = (d: Date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(d)

/**
 * Jours de CALENDRIER (Paris) entre l'enregistrement et maintenant. Un âge en
 * tranches de 24 h disait « changée aujourd'hui » d'une clé posée la veille
 * à 10 h et regardée le lendemain à 20 h.
 */
export function joursCalendaires(depuisIso: string, maintenant: Date = new Date()): number {
  const a = Date.parse(`${jourParis(new Date(depuisIso))}T00:00:00Z`)
  const b = Date.parse(`${jourParis(maintenant)}T00:00:00Z`)
  return Math.max(0, Math.round((b - a) / 86_400_000))
}

/** Âge lisible : « aujourd'hui », « hier », puis un nombre de jours. */
export function ageCle(enregistreeLe: string, maintenant: Date = new Date()): { jours: number; panneau: string; carte: string } {
  const j = joursCalendaires(enregistreeLe, maintenant)
  if (j === 0) return { jours: 0, panneau: 'Changée aujourd’hui', carte: 'changée aujourd’hui' }
  if (j === 1) return { jours: 1, panneau: 'Changée hier', carte: 'changée hier' }
  return { jours: j, panneau: `${j} jours`, carte: `changée il y a ${j} j` }
}

/**
 * LA LIGNE « ÂGE » DU PANNEAU — un seul âge, une date, le conseil (16/10/2026).
 *
 * Le panneau écrivait « 55 jours — Secret changé il y a 54 jours » : l'âge de
 * l'écran (jours de calendrier) à côté du conseil du serveur, qui recomptait
 * en tranches de 24 h. Le serveur compte désormais au calendrier de Paris,
 * et l'écran prend SON nombre (`age_jours`) quand il le sert : un seul
 * calcul, pour l'âge comme pour l'échéance. Le conseil affiché est la
 * `consigne`, qui ne répète pas l'âge.
 *
 * « 55 jours (enregistrée le 22/08/2026) — À jour : la rotation conseillée
 * intervient dans 310 jours. »
 */
export function ligneAge(e: Pick<EtatCle, 'enregistree_le' | 'rotation'>, maintenant: Date = new Date()): string | null {
  if (e.enregistree_le === null) return null
  const j = e.rotation.age_jours ?? joursCalendaires(e.enregistree_le, maintenant)
  const age = j === 0 ? 'Changée aujourd’hui' : j === 1 ? 'Changée hier' : `${j} jours`
  const le = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric' })
    .format(new Date(e.enregistree_le))
  // Un serveur d'avant la consigne : son conseil répète l'âge, on le tait
  // plutôt que d'afficher deux âges (la pastille garde le conseil en bulle).
  const conseil = e.rotation.consigne ?? null
  return `${age} (enregistrée le ${le})${conseil ? ` — ${conseil}` : ''}`
}

/**
 * Nom à donner à la NOUVELLE clé chez le fournisseur : daté, pour ne jamais
 * porter le même nom que celle qu'elle remplace. Avec un nom fixe
 * (« Gestion »), l'étape « supprimez celle nommée Gestion » pouvait désigner
 * la clé qu'on venait de créer — et rouvrir la panne que le panneau évite.
 */
export function nomNouvelleCle(maintenant: Date = new Date()): string {
  return `Gestion ${jourParis(maintenant).slice(0, 7)}`
}

/**
 * L'adresse que lit le module Access est celle de son API développeur
 * (port 12445) : l'ouvrir dans un navigateur donne une erreur, pas l'écran
 * où l'on crée un jeton. On ne garde que la console.
 */
export function consoleAccess(adresseApi: string | null): string | null {
  if (!adresseApi) return null
  try {
    const u = new URL(adresseApi)
    return `${u.protocol}//${u.hostname}`
  } catch { return null }
}

/* ─────────────────────────────────────────────────── mode d'emploi ─── */

/** Où créer la clé, et quelles AUTRES clés ne jamais supprimer. */
export interface Guide { ou: (adresse: string | null, nom: string) => string; autres: string; avant?: string }

export const GUIDES: Record<IntegrationACle, Guide> = {
  unifi_network: {
    ou: (a, nom) => `Dans la console UniFi${a ? ` (${a})` : ''} : Network → Paramètres → Control Plane → Intégrations → « Créer une clé d’API ». Nommez-la « ${nom} ».`,
    autres: 'Les scripts du serveur et la Main Courante ont chacun leur propre clé Network : n’y touchez pas.',
  },
  unifi_protect: {
    ou: (a, nom) => `Dans la console du NVR${a ? ` (${a})` : ''} : Protect → Paramètres → Control Plane → Intégrations → « Créer une clé d’API ». Nommez-la « ${nom} ».`,
    autres: 'Protect demande sa propre clé : celle du réseau (UniFi Network) et le jeton UniFi Access n’y fonctionnent pas.',
  },
  home_assistant: {
    ou: (a, nom) => `Dans Home Assistant${a ? ` (${a})` : ''} : votre profil (en bas à gauche) → onglet Sécurité → Jetons d’accès longue durée → « Créer un jeton ». Nommez-le « ${nom} ».`,
    autres: 'Node-RED et le programme du PC du bureau ont leurs propres jetons : ne les supprimez pas, sinon leurs automatismes s’arrêtent.',
  },
  unifi_access: {
    ou: (a, nom) => {
      const c = consoleAccess(a)
      return `Dans la console UniFi${c ? ` (${c})` : ''}, ouvrez l’application Access : Paramètres → Général → Avancé → Jeton d’API → « Créer ». Nommez-le « ${nom} » et cochez uniquement les droits de consultation : utilisateurs, groupes, visiteurs, identifiants, règles, horaires, portes, appareils, journaux.`
    },
    autres: 'La Main Courante a son propre jeton et son webhook « Main courante LCR » : n’y touchez pas. Un ancien jeton « Debug - Claude », s’il existe encore, peut être supprimé.',
  },
  skello: {
    ou: () => 'Dans Skello : Paramètres → Intégrations → Logiciels d’analyse → API analytique → générer une nouvelle clé.',
    autres: 'Si Skello n’accepte qu’une clé à la fois, l’ancienne cesse de fonctionner dès la création de la nouvelle : enregistrez-la ici sans attendre.',
  },
  shotgun: {
    ou: () => 'Dans le Smartboard Shotgun : Settings → Integrations → Shotgun APIs → générer un jeton.',
    autres: 'La Main Courante utilise elle aussi un jeton Shotgun : ne le supprimez pas. Gestion ne révoque jamais rien chez Shotgun.',
    avant: 'Si Shotgun révoque l’ancien jeton à la création d’un nouveau, la Main Courante perdra sa liaison billetterie. Il faudra alors lui donner le nouveau jeton dans ses propres réglages.',
  },
  /* PRETIX (16/10/2026) — un jeton d'ÉQUIPE, et une équipe qui ne fait que
     LIRE les commandes. Un jeton de l'équipe des administrateurs ouvrirait à
     Gestion toute la billetterie : le mode d'emploi le dit avant tout. */
  pretix: {
    /* Les libellés EXACTS de Pretix 2026.3 en français (team_edit.html,
       permissions.py, fr/django.po) : il n'existe aucune case « Voir les
       commandes ». La seule case proche, « Toutes les autorisations relatives
       aux événements », donne le droit de modifier et de rembourser. */
    ou: (a, nom) => `Dans Pretix${a ? ` (${a})` : ''}, connecté en administrateur : menu de l’organisateur → « Équipes » → « Créer une nouvelle équipe », `
      + 'nommée « Gestion (lecture) ». Laissez DÉCOCHÉES « Toutes les autorisations d’organisateur » et « Toutes les autorisations relatives aux événements ». '
      + 'Cochez « Tous les événements (y compris ceux nouvellement créés) ». Dans « Autorisations de l’événement », ligne « Commandes » : « Tout afficher » ; '
      + 'toutes les autres lignes sur leur choix le plus faible (« Voir », « Accès impossible » ou « Interdit »), jamais « Afficher et modifier ». Enregistrez. '
      + `Sur la page de l’équipe, section « Tokens API » : « Nom du token » = « ${nom} », puis « Ajouter ». Copiez le token affiché : Pretix ne le montre qu’une fois.`,
    autres: 'Les téléphones de scan (pretixSCAN) ont leurs propres accès d’appareil : n’y touchez pas. Gestion ne modifie et ne révoque jamais rien dans Pretix.',
    avant: 'N’utilisez jamais un token de l’équipe des administrateurs, ni une équipe qui a « Toutes les autorisations relatives aux événements » : il ouvrirait à Gestion la modification et le remboursement des commandes. L’équipe « Gestion (lecture) » n’a que « Commandes : Tout afficher ».',
  },
}

/**
 * Étape 4 : laquelle supprimer. Jamais un nom fixe que la nouvelle clé
 * porterait aussi — l'AUTRE clé de Gestion, la plus ancienne.
 */
export function etapeSuppression(nom: string, nomNouvelle: string): string {
  return `Supprimez l’ancienne clé de Gestion chez ${nom} — la plus ancienne, jamais « ${nomNouvelle} » que vous venez de créer. `
    + 'Si deux clés portent un nom proche, comparez leurs dates de création : la nouvelle est la plus récente. '
    + 'Et seulement quand « Dernière lecture réussie » ci-dessus dit « avec cette clé ».'
}

/* ─────────────────────────────────────────────── après l'enregistrement ─── */

/** Combien de temps l'écran relit l'état après un enregistrement. */
export const SUIVI_APRES_ENREGISTREMENT_MS = 60_000
export const SUIVI_PAS_MS = 4_000

export type PhaseApresEnregistrement = 'attente' | 'eprouvee' | 'echec' | 'sans_lecture'

/**
 * Où en est la clé qu'on vient d'enregistrer. L'invitation à supprimer
 * l'ancienne n'apparaît qu'en phase « eprouvee » : une lecture a réussi AVEC
 * la nouvelle. Avant, une clé fausse (ou jamais essayée) était suivie d'un
 * « supprimez l'ancienne » — l'ordre inverse, celui des 39 h de panne.
 */
export function phaseApresEnregistrement(
  e: Pick<EtatCle, 'enregistree_le' | 'reussite_depuis_enregistrement' | 'echec_depuis_enregistrement'>,
  ecouleMs: number,
  /** Date de la clé REMPLACÉE : un état qui la porte encore décrit l'ancienne
   *  clé (relu avant que l'enregistrement soit visible) — son « réussi » ne
   *  vaut rien pour la nouvelle. */
  dateCleRemplacee: string | null = null,
): PhaseApresEnregistrement {
  const parleDeLaNouvelle = e.enregistree_le !== null && e.enregistree_le !== dateCleRemplacee
  if (parleDeLaNouvelle && e.reussite_depuis_enregistrement) return 'eprouvee'
  if (parleDeLaNouvelle && e.echec_depuis_enregistrement) return 'echec'
  return ecouleMs >= SUIVI_APRES_ENREGISTREMENT_MS ? 'sans_lecture' : 'attente'
}

export function messageApresEnregistrement(phase: PhaseApresEnregistrement, e: Pick<EtatCle, 'nom' | 'echec_classe'>): { ton: 'ok' | 'ko' | 'doute'; texte: string } {
  switch (phase) {
    case 'eprouvee':
      return { ton: 'ok', texte: `Clé enregistrée et éprouvée : Gestion a lu ${e.nom} avec elle. Dernière étape : supprimez l’ancienne clé de Gestion chez ${e.nom} — pas celle que vous venez de créer.` }
    case 'echec':
      return {
        ton: 'ko',
        texte: (e.echec_classe === 'AUTH'
          ? `${e.nom} refuse la clé enregistrée. `
          : `La première lecture de ${e.nom} avec cette clé n’a pas abouti. `)
          + `Gestion ne lit plus ${e.nom} tant qu’une clé valable n’est pas enregistrée. Ne supprimez rien chez ${e.nom} : vérifiez la clé, testez-la, puis enregistrez-la de nouveau.`,
      }
    case 'sans_lecture':
      return { ton: 'doute', texte: `Aucune lecture n’a encore eu lieu avec cette clé. Ne supprimez rien chez ${e.nom} pour l’instant : revenez sur cette page dans quelques minutes — la dernière étape s’affichera quand une lecture aura réussi.` }
    default:
      return { ton: 'doute', texte: `Clé enregistrée. Gestion relit ${e.nom} avec elle… En attente de la première lecture : ne supprimez rien encore.` }
  }
}

/** Pastille d'en-tête : la lecture d'abord, l'âge ensuite. */
export function pastilleCle(e: EtatCle): { classe: string; texte: string; titre?: string } {
  if (!e.configuree) return { classe: 'lc-pill--warning', texte: 'Aucune clé' }
  if (e.echec_depuis_enregistrement) return { classe: 'lc-pill--warning', texte: 'Lecture en échec' }
  if (!e.reussite_depuis_enregistrement) return { classe: 'lc-pill--idle', texte: 'Pas encore éprouvée' }
  if (e.rotation.etat === 'EN_RETARD') return { classe: 'lc-pill--warning', texte: 'À renouveler', titre: e.rotation.conseil }
  if (e.rotation.etat === 'A_PREVOIR') return { classe: 'lc-pill--idle', texte: 'Renouvellement à prévoir', titre: e.rotation.conseil }
  return { classe: 'lc-pill--ok', texte: 'En place' }
}

/* ──────────────────────────────────── depuis une page Infrastructure ─── */

/**
 * Page d'intégration qui porte le panneau, pour chaque source de la
 * supervision qui s'ouvre par une clé. L'identifiant d'adresse n'est PAS le
 * même des deux côtés : /infrastructure/billetterie ↔ /settings/integrations/shotgun.
 */
export const PAGE_CLE_PAR_SOURCE: Readonly<Record<string, string>> = {
  'unifi-network': '/settings/integrations/unifi-network',
  'unifi-protect': '/settings/integrations/unifi-protect',
  'home-assistant': '/settings/integrations/home-assistant',
  billetterie: '/settings/integrations/shotgun',
  'billetterie-pretix': '/settings/integrations/pretix',
}

/** Sources dont la page Infrastructure garde son formulaire de dépannage
 *  (celles qu'accepte PUT /api/v1/infrastructure/:slug/credential). */
export const SOURCES_FORMULAIRE_CLE: ReadonlySet<string> = new Set(['unifi-network', 'unifi-protect', 'home-assistant'])
